import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { DOODLE_ICONS, DOODLE_WORDS } from "@/lib/doodles";
import { PATH_A, PATH_B } from "@/lib/indiaPath";

const OUTLINE = (() => {
  const pts = (p) => [...p.matchAll(/[ML]([\d.]+) ([\d.]+)/g)].map((m) => [+m[1], +m[2]]);
  return [...pts(PATH_A), ...pts(PATH_B).reverse()];
})();

const insidePoly = (x, y, poly) => {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i], [xj, yj] = poly[j];
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
};

const distToPoly = (x, y, poly) => {
  let best = Infinity;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [x1, y1] = poly[j], [x2, y2] = poly[i];
    const dx = x2 - x1, dy = y2 - y1;
    const t = Math.max(0, Math.min(1, ((x - x1) * dx + (y - y1) * dy) / (dx * dx + dy * dy || 1)));
    best = Math.min(best, Math.hypot(x - (x1 + t * dx), y - (y1 + t * dy)));
  }
  return best;
};

const COLORS = ["#B85A1C", "#1E3A8A", "#1F7A47", "#7A4A22", "#8A3B2E"];
const rnd = (a, b) => a + Math.random() * (b - a);
const pickOne = (arr) => arr[Math.floor(Math.random() * arr.length)];

const pick = () =>
  Math.random() < 0.32
    ? { type: "word", key: `w-${Date.now()}-${Math.random()}`, ...pickOne(DOODLE_WORDS) }
    : { type: "icon", key: `i-${Date.now()}-${Math.random()}`, ...pickOne(DOODLE_ICONS) };

const DrawnIcon = ({ icon, size, rot }) => {
  const refs = useRef([]);
  const [plan, setPlan] = useState(null);
  const dur = 2.6;

  useLayoutEffect(() => {
    const els = refs.current.filter(Boolean);
    const lens = els.map((el) => (el.getTotalLength ? el.getTotalLength() : 0));
    const total = lens.reduce((a, b) => a + b, 0) || 1;
    const xs = [], ys = [], times = [];
    let acc = 0;
    const segs = els.map((el, i) => {
      const n = Math.max(4, Math.round(lens[i] / 1.5));
      for (let k = 0; k <= n; k++) {
        const p = el.getPointAtLength((lens[i] * k) / n);
        xs.push(p.x); ys.push(p.y); times.push((acc + (lens[i] * k) / n) / total);
      }
      const seg = { delay: (acc / total) * dur, duration: Math.max(0.15, (lens[i] / total) * dur) };
      acc += lens[i];
      return seg;
    });
    setPlan({ xs, ys, times, segs });
  }, []);

  return (
    <motion.svg
      viewBox={`0 0 ${icon.vb} ${icon.vb}`}
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={icon.vb === 24 ? 1.5 : 1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ overflow: "visible" }}
      initial={{ opacity: 0, rotate: rot - 4 }}
      animate={{ opacity: 0.7, rotate: rot, y: [0, -5, 0] }}
      exit={{ opacity: 0, scale: 0.96, transition: { duration: 1 } }}
      transition={{ opacity: { duration: 0.5 }, y: { duration: 7, repeat: Infinity, ease: "easeInOut" } }}
      data-testid="doodle-icon"
    >
      {icon.nodes.map(([tag, attrs], i) => {
        const Tag = motion[tag];
        const seg = plan?.segs[i];
        return (
          <Tag
            key={i}
            {...attrs}
            ref={(el) => (refs.current[i] = el)}
            initial={{ pathLength: 0, opacity: 0 }}
            animate={plan ? { pathLength: 1, opacity: 1 } : { pathLength: 0, opacity: 0 }}
            transition={seg ? { pathLength: { delay: seg.delay, duration: seg.duration, ease: "linear" }, opacity: { delay: seg.delay, duration: 0.01 } } : { duration: 0 }}
          />
        );
      })}
      {plan && (
        <motion.circle
          r={icon.vb === 24 ? 0.9 : 1.6}
          fill="currentColor"
          stroke="none"
          initial={{ cx: plan.xs[0], cy: plan.ys[0], opacity: 1 }}
          animate={{ cx: plan.xs, cy: plan.ys, opacity: [1, 1, 0] }}
          transition={{ cx: { duration: dur, times: plan.times, ease: "linear" }, cy: { duration: dur, times: plan.times, ease: "linear" }, opacity: { duration: dur + 0.4, times: [0, 0.96, 1] } }}
        />
      )}
    </motion.svg>
  );
};

const DrawnWord = ({ word, size, rot }) => (
  <motion.span
    lang={word.lang}
    className={`block whitespace-nowrap select-none ${word.lang === "en" ? "font-display italic" : "font-sans"}`}
    style={{ fontSize: size * 0.48, rotate: `${rot}deg` }}
    initial={{ opacity: 0, clipPath: "inset(0 100% 0 0)" }}
    animate={{ opacity: 0.72, clipPath: "inset(0 0% 0 0)", y: [0, -4, 0] }}
    exit={{ opacity: 0, transition: { duration: 1 } }}
    transition={{ opacity: { duration: 0.4 }, clipPath: { duration: 1.8, ease: "easeInOut" }, y: { duration: 7, repeat: Infinity, ease: "easeInOut" } }}
    data-testid="doodle-word"
  >
    {word.t}
  </motion.span>
);

const Slot = ({ x, y, color, size }) => {
  const [item, setItem] = useState(null);
  const timer = useRef(null);
  const rot = useMemo(() => rnd(-10, 10), [item]);

  useEffect(() => {
    const cycle = () => {
      setItem(pick());
      timer.current = setTimeout(() => {
        setItem(null);
        timer.current = setTimeout(cycle, rnd(2500, 6500));
      }, rnd(6000, 9000));
    };
    timer.current = setTimeout(cycle, rnd(0, 9000));
    return () => clearTimeout(timer.current);
  }, []);

  return (
    <div className="absolute -translate-x-1/2 -translate-y-1/2" style={{ left: x, top: y, color }}>
      <AnimatePresence>
        {item && (item.type === "icon" ? <DrawnIcon key={item.key} icon={item} size={size} rot={rot} /> : <DrawnWord key={item.key} word={item} size={size} rot={rot} />)}
      </AnimatePresence>
    </div>
  );
};

export const HeroDoodles = ({ heroRef, mapRef }) => {
  const [slots, setSlots] = useState([]);

  useEffect(() => {
    const compute = () => {
      const hero = heroRef.current?.getBoundingClientRect();
      const map = mapRef.current?.getBoundingClientRect();
      if (!hero || !map) return setSlots([]);
      const W = hero.width, H = hero.height;
      const mobile = W < 900;
      const margin = mobile ? 30 : 84;
      const cell = mobile ? 105 : 150;
      const edge = mobile ? 40 : 36;
      const topLimit = mobile ? 118 : 160;
      const sx = map.width / 600, sy = map.height / 596;
      const poly = OUTLINE.map(([px, py]) => [map.left - hero.left + px * sx, map.top - hero.top + py * sy]);
      const nearMap = (x, y) => insidePoly(x, y, poly) || distToPoly(x, y, poly) < margin;
      const pts = [];
      for (let gy = topLimit; gy + cell * 0.6 < H - edge; gy += cell) {
        for (let gx = edge; gx + cell * 0.6 < W - edge; gx += cell) {
          if (Math.random() < 0.15) continue;
          const x = gx + cell * 0.2 + Math.random() * cell * 0.6;
          const y = gy + cell * 0.2 + Math.random() * Math.min(cell * 0.6, H - edge - gy - cell * 0.2);
          if (nearMap(x, y)) continue;
          if (x > W - edge - 30) continue;
          pts.push({ x, y, color: COLORS[Math.floor(Math.random() * COLORS.length)], size: mobile ? rnd(30, 38) : rnd(40, 54) });
        }
      }
      setSlots(pts);
    };
    const t = setTimeout(compute, 80);
    window.addEventListener("resize", compute);
    return () => { clearTimeout(t); window.removeEventListener("resize", compute); };
  }, [heroRef, mapRef]);

  return (
    <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden" aria-hidden="true" data-testid="doodle-layer">
      {slots.map((s, i) => <Slot key={`${i}-${s.x.toFixed(0)}-${s.y.toFixed(0)}`} {...s} />)}
    </div>
  );
};

export const AmbientGlow = () => (
  <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden hidden md:block" aria-hidden="true">
    <motion.div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full blur-[120px]" style={{ background: "radial-gradient(circle, rgba(232,106,34,0.14), transparent 70%)" }} animate={{ x: [0, 60, 0], y: [0, 40, 0] }} transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }} />
    <motion.div className="absolute -bottom-40 -right-40 w-[560px] h-[560px] rounded-full blur-[120px]" style={{ background: "radial-gradient(circle, rgba(35,133,79,0.14), transparent 70%)" }} animate={{ x: [0, -50, 0], y: [0, -40, 0] }} transition={{ duration: 26, repeat: Infinity, ease: "easeInOut" }} />
  </div>
);
